import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { useOnClickOutside } from '../../../helpers/hooks';

const moment = extendMoment(Moment);

const DatePicker = ({
  position,
  startDateLabel,
  endDateLabel,
  startDate,
  endDate,
  focusedInput,
  onFocusChange,
  onSelectDate,
  onClose
}) => {
  const ref = useRef();
  const today = useMemo(() => moment(), []);
  const [month, setMonth] = useState(moment(today).startOf('month'));
  const [calendar, setCalendar] = useState([]);

  useOnClickOutside(ref, onClose);

  const buildMonth = useCallback(() => {
    const firstDay = moment(month).startOf('month');
    const endDay = moment(month).endOf('month');
    const monthRange = moment.range(firstDay, endDay);

    // Get all the weeks during the current month
    const weeks = [];
    Array.from(monthRange.by('days')).forEach(day => {
      if (!weeks.includes(day.week())) {
        weeks.push(day.week());
      }
    });

    while (weeks.length < 6) {
      weeks.push(weeks[weeks.length - 1] + 1);
    }

    const parsedWeeks = [];
    const currentYear = firstDay.year();
    const nextYear = currentYear + 1;

    weeks.forEach((week, index) => {
      parsedWeeks.push({
        week,
        year: (
          index > 0 &&
          (week < weeks[index -1] || parsedWeeks[index -1].year === nextYear)
        ) ? nextYear : currentYear
      })
    });

    const cal = [];
    parsedWeeks.forEach(({ week, year }) => {
      const firstWeekDay = moment().year(year).week(week).startOf('week');
      const lastWeekDay = moment().year(year).week(week).endOf('week');
      const weekRange = moment.range(firstWeekDay, lastWeekDay);
      cal.push(weekRange);
    });

    setCalendar(cal);
  }, [month]);

  useEffect(() => {
    buildMonth();
  }, [buildMonth]);

  const handlePreviousMonth = () => {
    setMonth(moment(month).subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setMonth(moment(month).add(1, 'month'));
  };

  const handleDateClick = date => {
    if (!startDate || moment(startDate).diff(date, 'days') >= 0) {
      onSelectDate(date, null);
      onFocusChange('end');
    } else if (moment(startDate).diff(date, 'days') >= 0) {
      onSelectDate(date, null);
      onFocusChange('end');
    } else if (focusedInput === 'start') {
      if (focusedInput === 'start' && moment(endDate).diff(date, 'days') <= 0) {
        onSelectDate(date, null);
      } else {
        onSelectDate(date, endDate);
      }
      onFocusChange('end');
    } else {
      onSelectDate(startDate, date);
    }
  };

  const getLabel = () => {
    if (startDate) {
      if (endDate) {
        return `${moment(startDate).format('MMMM D')}${
          !moment(startDate).isSame(endDate, 'year')
            ? `, ${moment(startDate).format('YYYY')}`
            : ''
        } - ${moment(endDate).format('MMMM D, YYYY')}`;
      }

      return endDateLabel;
    }
    return startDateLabel;
  };

  return (
    <>
      <div className="date-picker__overlay"></div>
      <div
        className={classNames('date-picker', `date-picker--${position}`)}
        ref={ref}
      >
        <div className="date-picker__header">
          <p>{getLabel()}</p>
        </div>
        <div className="date-picker__body">
          <table
            className="date-picker__calendar"
            cellSpacing="0"
            cellPadding="0"
          >
            <thead>
              <tr className="date-picker__nav">
                <th>
                  <button
                    type="button"
                    className="date-picker__nav-btn date-picker__nav-btn--prev"
                    disabled={moment().isSame(month, 'month')}
                    onClick={handlePreviousMonth}
                  >
                    <svg viewBox="0 0 180 320">
                      <title>previous</title>
                      <use xlinkHref="#chevron-right" />
                    </svg>
                  </button>
                </th>
                <th colSpan="5">
                  <p className="date-picker__nav-month">
                    {moment(month).format('MMMM YYYY')}
                  </p>
                </th>
                <th>
                  <button
                    type="button"
                    className="date-picker__nav-btn date-picker__nav-btn--next"
                    onClick={handleNextMonth}
                  >
                    <svg viewBox="0 0 180 320">
                      <title>next</title>
                      <use xlinkHref="#chevron-right" />
                    </svg>
                  </button>
                </th>
              </tr>
              <tr>
                <th>S</th>
                <th>M</th>
                <th>T</th>
                <th>W</th>
                <th>T</th>
                <th>F</th>
                <th>S</th>
              </tr>
            </thead>
            <tbody className="date-picker__calendar-body">
              {calendar.map((week, i) => {
                const days = Array.from(week.by('day'));
                return (
                  <tr key={`week-${i}`}>
                    {days.map((day, j) => (
                      <td
                        key={`day-${j}`}
                        className={classNames(
                          {
                            selected:
                              moment(day).isSame(startDate) ||
                              moment(day).isSame(endDate)
                          },
                          {
                            'start-date':
                              moment(day).isSame(startDate) && endDate
                          },
                          {
                            'end-date': moment(day).isSame(endDate) && startDate
                          },
                          {
                            between:
                              startDate &&
                              endDate &&
                              moment(day).isBetween(startDate, endDate)
                          }
                        )}
                      >
                        <button
                          type="button"
                          disabled={
                            !moment(month).isSame(day, 'month') ||
                            moment().diff(day, 'days') > 0
                          }
                          onClick={() => handleDateClick(day)}
                        >
                          {day.date()}
                        </button>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

DatePicker.propTypes = {
  position: PropTypes.string.isRequired,
  startDateLabel: PropTypes.string.isRequired,
  endDateLabel: PropTypes.string.isRequired,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  focusedInput: PropTypes.string,
  onFocusChange: PropTypes.func,
  onSelectDate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default DatePicker;
